import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { rankingsApi, rankingItemsApi } from '../api';
import { Ranking, RankingItem } from '../types';
import '../styles/main.css';
import '../styles/rankingDetail.css';
import tagApi from '../api/tagApi';
// 使用 require 导入图片
const starIcon = require('../assets/images/star_48.png');
const forkIcon = require('../assets/images/fork_48.png');
const viewIcon = require('../assets/images/click_48.png');
const back = require('../assets/images/back_48.png');
const add = require('../assets/images/add_48.png');
const close = require('../assets/images/close_48.png');

// 扩展 RankingItem 类型以包含所需的属性
interface EnhancedRankingItem extends RankingItem {
  stars?: number;
  forks?: number;
  updateCycle?: string;
  status?: string;
  mediaUrl?: string;
  url?: string;
  clickCount?: number; // 添加点击计数属性
  clkCnt?: number;     // 添加后端返回的点击次数字段
  tags?: string;       // 添加标签字段
}

// 扩展 Ranking 类型以包含点击计数和标签
interface EnhancedRanking extends Ranking {
  clickCount?: number;
  clkCnt?: number;     // 添加后端返回的点击次数字段
  tags?: string;       // 添加标签字段
}

// 添加一个辅助函数来解析标签字符串
const parseTags = (tagsString?: string): { name: string; cnt: number }[] => {
  if (!tagsString || tagsString.trim() === '') {
    return [];
  }
  
  try {
    // 按照 \x01 分隔不同的标签（使用十六进制转义序列替代八进制）
    const tagPairs = tagsString.split('\x01');
    
    return tagPairs
      .map(pair => {
        // 按照 \x02 分隔标签名和计数（使用十六进制转义序列替代八进制）
        const [name, cntStr] = pair.split('\x02');
        return {
          name: name.trim(),
          cnt: parseInt(cntStr, 10) || 0
        };
      })
      // 按照计数倒序排序
      .sort((a, b) => b.cnt - a.cnt);
  } catch (error) {
    console.error('解析标签失败:', error, tagsString);
    return [];
  }
};

const RankingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [ranking, setRanking] = useState<EnhancedRanking | null>(null);
  const [items, setItems] = useState<EnhancedRankingItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // 添加新状态
  const [showTagModal, setShowTagModal] = useState<boolean>(false);
  const [currentItemId, setCurrentItemId] = useState<number | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState<string>('');
  // 添加状态来跟踪鼠标悬停的标签
  const [hoveredTagId, setHoveredTagId] = useState<string | null>(null);
  // 添加一个状态来显示提示信息
  const [tagMessage, setTagMessage] = useState<{text: string, type: 'success' | 'error' | 'info', visible: boolean}>({
    text: '',
    type: 'info',
    visible: false
  });
  // 添加状态来存储已删除的项目ID
  const [deletedItemIds, setDeletedItemIds] = useState<number[]>([]);

  // 记录排行榜点击
  const recordRankingClick = async (rankingId: string | number) => {
    try {
      const response = await fetch(`/api/clicks/rankingList/${rankingId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      console.log('排行榜点击记录结果:', data);
    } catch (error) {
      console.error('记录排行榜点击失败:', error);
    }
  };

  // 记录排行榜项目点击
  const recordItemClick = async (itemId: number) => {
    try {
      // 只更新一个计数器，避免重复计数
      setItems(prevItems => 
        prevItems.map(item => 
          item.id === itemId 
            ? { ...item, clickCount: (item.clickCount || 0) + 1 } 
            : item
        )
      );
      
      const response = await fetch(`/api/clicks/rankingItem/${itemId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      console.log('项目点击记录结果:', data);
    } catch (error) {
      console.error('记录项目点击失败:', error);
    }
  };

  // 处理项目点击
  const handleItemClick = (itemId: number, url?: string) => {
    // 确保只调用一次记录函数
    recordItemClick(itemId);
    
    // 如果有URL，则跳转
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // 修改 fetchAllTags 函数，使其获取特定项目的标签
  const fetchItemTags = async (itemId: number) => {
    if (!itemId) return;
    
    try {
      // 使用 tagApi 获取项目标签
      const response = await tagApi.getRankingItemTags(itemId);
      const tagResponses = response.data;
      
      // 从响应中提取标签名称
      const tagNames = tagResponses.map(tag => tag.name);
      setAllTags(tagNames);
      
      // 如果API没有返回标签，则从项目的tags属性中提取
      if (tagNames.length === 0) {
        const currentItem = items.find(item => item.id === itemId);
        if (currentItem && currentItem.tags) {
          const parsedTags = parseTags(currentItem.tags);
          setAllTags(parsedTags.map(tag => tag.name));
        } else {
          setAllTags([]);
        }
      }
    } catch (error) {
      console.error('获取项目标签失败:', error);
      // 从当前项目中提取标签作为备选
      const currentItem = items.find(item => item.id === itemId);
      if (currentItem && currentItem.tags) {
        const parsedTags = parseTags(currentItem.tags);
        setAllTags(parsedTags.map(tag => tag.name));
      } else {
        setAllTags([]);
      }
    }
  };

  // 修改 handleAddTagClick 函数，使用新的 fetchItemTags 函数
  const handleAddTagClick = (itemId: number) => {
    setCurrentItemId(itemId);
    setNewTag('');
    fetchItemTags(itemId); // 使用新函数获取特定项目的标签
    setShowTagModal(true);
  };

  // 处理添加标签
  const handleAddTag = async (tag: string) => {
    if (!currentItemId) return;
    
    try {
      // 使用 tagApi 添加标签
      await tagApi.addRankingItemTag(currentItemId, tag);
      
      // 更新本地状态
      setItems(prevItems => 
        prevItems.map(item => {
          if (item.id === currentItemId) {
            // 解析现有标签
            const existingTags = parseTags(item.tags);
            
            // 检查标签是否已存在
            const existingTagIndex = existingTags.findIndex(t => t.name === tag);
            
            if (existingTagIndex >= 0) {
              // 如果标签已存在，增加计数
              existingTags[existingTagIndex].cnt += 1;
            } else {
              // 如果标签不存在，添加新标签
              existingTags.push({ name: tag, cnt: 1 });
            }
            
            // 重新格式化标签字符串（使用十六进制转义序列）
            const newTagsString = existingTags
              .map(t => `${t.name}\x02${t.cnt}`)
              .join('\x01');
            
            return { ...item, tags: newTagsString };
          }
          return item;
        })
      );
      
      // 如果是新标签，添加到所有标签列表
      if (!allTags.includes(tag)) {
        setAllTags(prev => [...prev, tag]);
      }
      
    } catch (error) {
      console.error('添加标签失败:', error);
    }
  };

  // 处理提交新标签
  const handleSubmitNewTag = () => {
    if (newTag.trim()) {
      handleAddTag(newTag.trim());
      setNewTag('');
    }
  };

  // 修改 handleRemoveTag 函数
  const handleRemoveTag = async (itemId: number, tagName: string) => {
    try {
      // 使用 tagApi 移除标签
      const response = await tagApi.removeTag('RANKING_ITEM', itemId, tagName);
      const result = response.data;
      
      // 检查删除结果
      if (result === false) {
        // 显示提示信息
        setTagMessage({
          text: `标签"${tagName}"由多人添加，已记录您的删除请求，系统将综合评估后决定是否删除。`,
          type: 'info',
          visible: true
        });
        
        // 3秒后自动隐藏提示
        setTimeout(() => {
          setTagMessage(prev => ({...prev, visible: false}));
        }, 3000);
        
        return; // 不更新本地状态
      }
      
      // 删除成功，更新本地状态
      setItems(prevItems => 
        prevItems.map(item => {
          if (item.id === itemId && item.tags) {
            // 解析现有标签
            const existingTags = parseTags(item.tags);
            
            // 移除指定标签
            const filteredTags = existingTags.filter(t => t.name !== tagName);
            
            // 重新格式化标签字符串
            const newTagsString = filteredTags
              .map(t => `${t.name}\x02${t.cnt}`)
              .join('\x01');
            
            return { ...item, tags: newTagsString };
          }
          return item;
        })
      );
      
      // 显示成功提示
      setTagMessage({
        text: `标签"${tagName}"已成功删除`,
        type: 'success',
        visible: true
      });
      
      // 3秒后自动隐藏提示
      setTimeout(() => {
        setTagMessage(prev => ({...prev, visible: false}));
      }, 3000);
      
    } catch (error) {
      console.error('移除标签失败:', error);
      
      // 显示错误提示
      setTagMessage({
        text: '删除标签失败，请稍后重试',
        type: 'error',
        visible: true
      });
      
      // 3秒后自动隐藏提示
      setTimeout(() => {
        setTagMessage(prev => ({...prev, visible: false}));
      }, 3000);
    }
  };

  // 处理删除项目
  const handleDeleteItem = (itemId: number) => {
    // 更新本地状态，添加到已删除项目列表
    setDeletedItemIds(prev => {
      const newDeletedIds = [...prev, itemId];
      
      // 将删除记录保存到localStorage中
      try {
        const tokenKey = `deleted_items_${id}`;
        localStorage.setItem(tokenKey, JSON.stringify(newDeletedIds));
      } catch (error) {
        console.error('保存删除记录失败:', error);
      }
      
      return newDeletedIds;
    });
    
    // 显示成功提示
    setTagMessage({
      text: '项目已成功隐藏，下次访问将不再显示',
      type: 'success',
      visible: true
    });
    
    // 3秒后自动隐藏提示
    setTimeout(() => {
      setTagMessage(prev => ({...prev, visible: false}));
    }, 3000);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const [rankingRes, itemsRes] = await Promise.all([
          rankingsApi.getById(id),
          rankingItemsApi.getByRankingId(id)
        ]);
        
        setRanking(rankingRes.data);
        setItems(itemsRes.data as EnhancedRankingItem[]);
        
        // 记录排行榜被查看
        recordRankingClick(id);
        
        // 从localStorage中获取已删除的项目ID
        try {
          const tokenKey = `deleted_items_${id}`;
          const savedDeletedIds = localStorage.getItem(tokenKey);
          if (savedDeletedIds) {
            setDeletedItemIds(JSON.parse(savedDeletedIds));
          }
        } catch (error) {
          console.error('获取删除记录失败:', error);
        }
      } catch (error) {
        console.error('获取榜单详情失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <>
        <div className="loading">加载中...</div>
      </>
    );
  }

  if (!ranking) {
    return (
      <>
        <div className="empty-state">未找到榜单</div>
      </>
    );
  }

  return (
    <>
      {/* 添加内联样式到组件顶层 */}
      <style>
        {`
          .item-card:hover .delete-button {
            opacity: 1 !important;
          }
        `}
      </style>
      
      <div className="container" style={{ 
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 15px'
      }}>
        <div className="ranking-detail">
          <div className="ranking-header">
            {ranking.coverUrl && (
              <div className="cover-container" style={{ 
                position: 'relative',
                height: '180px',
                overflow: 'hidden',
                borderRadius: '8px'
              }}>
                <Link 
                  to="/" 
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    zIndex: 10,
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px'
                  }}
                >
                  <img 
                    src={back} 
                    alt="返回" 
                    style={{ 
                      width: '100%', 
                      height: '100%',
                      filter: 'brightness(0) invert(1)'
                    }} 
                  />
                </Link>
                
                <img 
                  src={ranking.coverUrl} 
                  alt={ranking.title} 
                  style={{ 
                    width: '100%', 
                    height: '180px', 
                    objectFit: 'cover'
                  }} 
                />
                <div className="cover-overlay" style={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  backgroundColor: 'rgba(0, 0, 0, 0.5)', 
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  padding: '20px'
                }}>
                  <h1 style={{ color: '#ffffff', margin: '0 0 10px 0' }}>{ranking.title}</h1>
                  <p style={{ color: '#ffffff', margin: 0 }}>{ranking.description}</p>
                </div>
              </div>
            )}
            {!ranking.coverUrl && (
              <>
                <div style={{ position: 'relative', marginBottom: '20px' }}>
                  <Link 
                    to="/" 
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      zIndex: 10,
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px'
                    }}
                  >
                    <img 
                      src={back} 
                      alt="返回" 
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        filter: 'brightness(0)'
                      }} 
                    />
                  </Link>
                  <h1 style={{ marginLeft: '40px' }}>{ranking.title}</h1>
                  <p>{ranking.description}</p>
                </div>
              </>
            )}
            <div className="tags">
              {ranking.type && <span className="tag">{ranking.type}</span>}
            </div>
          </div>

          <div className="items-list">
            {items.length === 0 ? (
              <div className="empty-state">暂无排名项目</div>
            ) : (
              items
                .sort((a, b) => {
                  // 首先按照 stars 数量降序排序
                  if (a.stars !== undefined && b.stars !== undefined) {
                    return b.stars - a.stars;
                  }
                  // 如果没有 stars，则按照原来的 position 排序
                  return (a.position || 0) - (b.position || 0);
                })
                // 过滤掉已删除的项目
                .filter(item => !deletedItemIds.includes(item.id))
                .map((item, index) => (
                <div 
                  className="item-card" 
                  key={item.id}
                  style={{ 
                    cursor: item.url ? 'pointer' : 'default',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    // 找到当前卡片中的删除按钮并显示
                    const deleteBtn = e.currentTarget.querySelector('.delete-button') as HTMLElement;
                    if (deleteBtn) deleteBtn.style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    // 找到当前卡片中的删除按钮并隐藏
                    const deleteBtn = e.currentTarget.querySelector('.delete-button') as HTMLElement;
                    if (deleteBtn) deleteBtn.style.opacity = '0';
                  }}
                >
                  {/* 删除按钮 */}
                  <button
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteItem(item.id);
                    }}
                    style={{
                      position: 'absolute',
                      top: '0px',
                      right: '0px',
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: '#f44336',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      cursor: 'pointer',
                      zIndex: 10,
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                    }}
                  >
                    <img 
                      src={close} 
                      alt="删除" 
                      style={{ 
                        width: '100%',
                        height: '100%',
                        filter: 'brightness(0) invert(1)' // 使图标变为白色
                      }}
                    />
                  </button>
                  
                  <div className="item-rank">#{index + 1}</div>
                  <div className="item-content">
                    {/* 左侧内容区域 - 60% */}
                    <div className="item-content-left" 
                      onClick={() => handleItemClick(item.id, item.url)}
                      style={{ 
                        width: '60%', 
                        float: 'left',
                        paddingRight: '10px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <h3 className="item-title">{item.name || item.title}</h3>
                      <p className="item-description">{item.description}</p>
                      <div className="item-stats">
                        {item.stars !== undefined && (
                          <span className="stat-item">
                            <img 
                              src={starIcon} 
                              alt="stars" 
                              className="icon-star" 
                              style={{ 
                                width: '16px', 
                                height: '16px', 
                                verticalAlign: 'middle',
                                filter: 'invert(72%) sepia(98%) saturate(1552%) hue-rotate(359deg) brightness(100%) contrast(105%)'
                              }} 
                            /> {item.stars >= 1000 
                              ? `${(item.stars / 1000).toFixed(1).replace(/\.0$/, '')}k` 
                              : item.stars}
                          </span>
                        )}
                        {item.forks !== undefined && (
                          <span className="stat-item">
                            <img 
                              src={forkIcon} 
                              alt="forks" 
                              className="icon-fork" 
                              style={{ 
                                width: '16px', 
                                height: '16px', 
                                verticalAlign: 'middle',
                                backgroundColor: '#FFEB3B',
                                padding: '2px',
                                borderRadius: '3px'
                              }} 
                            /> {item.forks >= 1000 
                              ? `${(item.forks / 1000).toFixed(1).replace(/\.0$/, '')}k` 
                              : item.forks}
                          </span>
                        )}
                        {(item.clkCnt !== undefined || item.clickCount !== undefined) && (
                          <span className="stat-item">
                            <img 
                              src={viewIcon} 
                              alt="views" 
                              className="icon-view" 
                              style={{ 
                                width: '16px', 
                                height: '16px', 
                                verticalAlign: 'middle',
                                marginRight: '2px',
                                filter: 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(90%) contrast(95%)'
                              }} 
                            /> {item.clkCnt || item.clickCount || 0} 次点击
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* 右侧标签区域 - 40% */}
                    <div className="item-content-right" style={{ 
                      width: '40%', 
                      float: 'left',
                      paddingLeft: '10px',
                      boxSizing: 'border-box',
                      borderLeft: '1px solid #eee',
                      minHeight: '100px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div className="tags-container">
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>标签</h4>
                        <div className="tags" style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          {/* 如果有标签，先展示所有标签 */}
                          {item.tags && parseTags(item.tags).map((tag, i) => {
                            const tagId = `${item.id}-${i}`;
                            return (
                              <span 
                                key={i} 
                                className="tag" 
                                style={{ 
                                  margin: '0',
                                  position: 'relative',
                                  display: 'inline-block'
                                }}
                                onMouseEnter={() => setHoveredTagId(tagId)}
                                onMouseLeave={() => setHoveredTagId(null)}
                              >
                                {tag.name}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTag(item.id, tag.name);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '-5px',
                                    right: '-5px',
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    fontSize: '10px',
                                    display: hoveredTagId === tagId ? 'flex' : 'none',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0,
                                    cursor: 'pointer',
                                    zIndex: 10
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                          
                          {/* 添加标签按钮（只显示加号图标） */}
                          <span 
                            className="add-tag-icon" 
                            onClick={(e) => {
                              e.stopPropagation(); // 阻止事件冒泡
                              handleAddTagClick(item.id);
                            }}
                            style={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              margin: '0',
                              backgroundColor: 'transparent',
                              borderRadius: '4px',
                              color: '#1976d2'
                            }}
                          >
                            <img 
                              src={add} 
                              alt="添加标签" 
                              style={{ 
                                width: '16px', 
                                height: '16px',
                                filter: 'invert(40%) sepia(57%) saturate(2728%) hue-rotate(196deg) brightness(95%) contrast(87%)'
                              }} 
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* 清除浮动 */}
                    <div style={{ clear: 'both' }}></div>
                  </div>
                  
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt={item.title} className="item-image" />
                  )}
                  {item.mediaUrl && !item.imageUrl && (
                    <img src={item.mediaUrl} alt={item.title} className="item-image" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* 添加提示消息 */}
        {tagMessage.visible && (
          <div 
            style={{
              position: 'fixed',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '10px 20px',
              borderRadius: '4px',
              backgroundColor: 
                tagMessage.type === 'success' ? '#4caf50' : 
                tagMessage.type === 'error' ? '#f44336' : 
                tagMessage.type === 'info' ? '#757575' : // 使用浅灰色表示信息提示
                '#2196f3',
              color: 'white',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              zIndex: 1000,
              maxWidth: '80%',
              textAlign: 'center'
            }}
          >
            {tagMessage.text}
          </div>
        )}
      </div>

      {/* 添加标签弹窗 */}
      {showTagModal && (
        <div 
          className="tag-modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowTagModal(false)} // 点击遮罩层关闭弹框
        >
          <div 
            className="tag-modal" 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()} // 阻止点击弹框内容时关闭弹框
          >
            {/* 关闭按钮 - 使用 close 图标 */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowTagModal(false);
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: '#f44336', // 添加红色背景
                borderRadius: '50%',   // 添加圆形边框
                cursor: 'pointer',
                padding: 0
              }}
            >
              <img 
                src={close} 
                alt="关闭" 
                style={{ 
                  width: '14px', 
                  height: '14px',
                  filter: 'brightness(0) invert(1)' // 使图标变为白色
                }} 
              />
            </button>
            
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>添加标签</h3>
            
            {/* 已有标签列表 - 简化标题 */}
            <div className="existing-tags" style={{ marginBottom: '20px' }}>
              <div style={{ 
                fontSize: '14px', 
                color: '#666', 
                marginBottom: '8px' 
              }}>
                已有标签
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {allTags.map((tag, index) => (
                  <span 
                    key={index} 
                    className="tag" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddTag(tag);
                      setShowTagModal(false); // 选择后自动关闭弹窗
                    }}
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: '#e9f5ff', // 与页面标签一致的背景色
                      color: '#3498db',           // 与页面标签一致的文字颜色
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '10px'            // 与页面标签一致的字体大小
                    }}
                  >
                    {tag}
                  </span>
                ))}
                {allTags.length === 0 && (
                  <span style={{ color: '#999', fontSize: '14px' }}>暂无标签</span>
                )}
              </div>
            </div>
            
            {/* 添加新标签 - 简化标题 */}
            <div className="new-tag">
              <div style={{ 
                fontSize: '14px', 
                color: '#666', 
                marginBottom: '8px' 
              }}>
                添加新标签
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="text" 
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="点击已有标签时，会强化已有标签不会被立即删除"
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmitNewTag();
                      setShowTagModal(false); // 添加后自动关闭弹窗
                    }
                  }}
                />
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubmitNewTag();
                    setShowTagModal(false); // 添加后自动关闭弹窗
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  添加
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RankingDetail; 